/**
 * Water: vanilla Minecraft (Java Edition) fluid rules.
 *
 * Water is a source block (amount 8) or flowing water with an amount of 1..7,
 * plus "falling" water fed from the block above. Like vanilla, a water block
 * only updates when it or a neighbour changes; the update runs 5 game ticks
 * later and:
 *  - recomputes a flowing block from its neighbours (it drains when its feed
 *    is gone, and turns into a source when two sources flank it over solid
 *    ground — the infinite water rule),
 *  - spreads down if it can, otherwise sideways towards the nearest drop
 *    within 4 blocks (all open sides when there is none).
 *
 * DOM-free: the height / flow helpers are shared by the mesher (worker), the
 * player physics and the simulation.
 */

import {
  AIR, COBBLE_ID, IS_LAVA, FALLING_WATER_ID, FLOWING_WATER_IDS, IS_SOLID, IS_WATER_SOURCE, LAVA_ID, OBSIDIAN_ID, SUGAR_CANE_ID,
  WATER_AMOUNT, WATER_ID,
} from '../blocks';

/** Game ticks between a water block being disturbed and its update. */
export const WATER_TICK_DELAY = 5;
/** How far water looks for a drop to flow towards. */
const SLOPE_FIND_DISTANCE = 4;
const NO_SLOPE = 1000;

const DX = [1, -1, 0, 0];
const DZ = [0, 0, 1, -1];
const OPPOSITE = [1, 0, 3, 2];

export type BlockReader = (x: number, y: number, z: number) => number;

/** Surface height of the water in a cell (0 when it holds none): full under more water, else amount / 9. */
export function waterHeight(read: BlockReader, x: number, y: number, z: number): number {
  const amount = WATER_AMOUNT[read(x, y, z)];
  if (amount === 0) return 0;
  return WATER_AMOUNT[read(x, y + 1, z)] > 0 ? 1 : amount / 9;
}

/**
 * Direction the water in cell (x, y, z) flows, as a unit vector written to
 * `out` (zero for still water). Water runs towards lower neighbours; falling
 * water against a wall also pulls down.
 */
export function waterFlow(read: BlockReader, x: number, y: number, z: number, out: number[]): void {
  const id = read(x, y, z);
  const own = WATER_AMOUNT[id] / 9;
  let fx = 0;
  let fz = 0;
  for (let d = 0; d < 4; d++) {
    const nx = x + DX[d];
    const nz = z + DZ[d];
    const nid = read(nx, y, nz);
    const h = WATER_AMOUNT[nid] / 9;
    let diff = 0;
    if (h === 0) {
      // open neighbour: water one block down still pulls the flow over the edge
      if (IS_SOLID[nid] === 0) {
        const below = WATER_AMOUNT[read(nx, y - 1, nz)] / 9;
        if (below > 0) diff = own - (below - 8 / 9);
      }
    } else {
      diff = own - h;
    }
    fx += DX[d] * diff;
    fz += DZ[d] * diff;
  }
  let fy = 0;
  if (id === FALLING_WATER_ID) {
    for (let d = 0; d < 4; d++) {
      if (IS_SOLID[read(x + DX[d], y, z + DZ[d])] === 1 || IS_SOLID[read(x + DX[d], y + 1, z + DZ[d])] === 1) {
        const len = Math.hypot(fx, fz);
        if (len > 1e-4) { fx /= len; fz /= len; } else { fx = 0; fz = 0; }
        fy = -6;
        break;
      }
    }
  }
  const len = Math.hypot(fx, fy, fz);
  if (len < 1e-4) {
    out[0] = out[1] = out[2] = 0;
  } else {
    out[0] = fx / len;
    out[1] = fy / len;
    out[2] = fz / len;
  }
}

/** What the simulation needs from the world. */
export interface FluidHost {
  getBlock(x: number, y: number, z: number): number;
  /** Is the cell inside the world and in a chunk that can be edited right now? */
  canEdit(x: number, y: number, z: number): boolean;
  /** Must report the change back through `FluidSim.blockChanged`. */
  setBlock(x: number, y: number, z: number, id: number): void;
}

export class FluidSim {
  /** Stop a game tick's updates after this long; the rest run next tick. */
  budgetMs = 6;

  private now = 0;
  // FIFO of scheduled updates (the delay is constant, so it stays sorted by due time)
  private readonly queue: number[] = []; // x, y, z, due
  private head = 0;
  private readonly scheduled = new Set<number>();
  private readonly spreadDirs: number[] = [];
  private readonly spreadIds: number[] = [];

  constructor(private readonly host: FluidHost) {}

  /** Updates waiting to run. */
  get pending(): number {
    return this.scheduled.size;
  }

  /** A block changed: water there and around it re-evaluates after the tick delay. */
  blockChanged(x: number, y: number, z: number): void {
    const host = this.host;
    if (WATER_AMOUNT[host.getBlock(x, y, z)] > 0) {
      this.schedule(x, y, z);
      // water beside or on top of lava hardens it (all lava here is still)
      this.hardenLava(x, y - 1, z);
      for (let d = 0; d < 4; d++) this.hardenLava(x + DX[d], y, z + DZ[d]);
    }
    this.scheduleIfWater(x, y - 1, z);
    this.scheduleIfWater(x, y + 1, z);
    for (let d = 0; d < 4; d++) this.scheduleIfWater(x + DX[d], y, z + DZ[d]);
  }

  /** One 20 Hz game tick: run every update that has come due. */
  tick(): void {
    this.now++;
    const q = this.queue;
    const t0 = performance.now();
    let n = 0;
    while (this.head < q.length && q[this.head + 3] <= this.now) {
      const x = q[this.head];
      const y = q[this.head + 1];
      const z = q[this.head + 2];
      this.head += 4;
      this.scheduled.delete(cellKey(x, y, z));
      this.update(x, y, z);
      if ((++n & 15) === 0 && performance.now() - t0 > this.budgetMs) break;
    }
    if (this.head === q.length) {
      q.length = 0;
      this.head = 0;
    } else if (this.head > 4096) {
      q.splice(0, this.head);
      this.head = 0;
    }
  }

  private schedule(x: number, y: number, z: number): void {
    const key = cellKey(x, y, z);
    if (this.scheduled.has(key)) return;
    this.scheduled.add(key);
    this.queue.push(x, y, z, this.now + WATER_TICK_DELAY);
  }

  private scheduleIfWater(x: number, y: number, z: number): void {
    if (WATER_AMOUNT[this.host.getBlock(x, y, z)] > 0) this.schedule(x, y, z);
  }

  private hardenLava(x: number, y: number, z: number): void {
    const id = this.host.getBlock(x, y, z);
    if (IS_LAVA[id] === 1 && this.host.canEdit(x, y, z)) {
      // vanilla: a lava source hardens to obsidian, flowing lava to cobblestone
      this.host.setBlock(x, y, z, id === LAVA_ID ? OBSIDIAN_ID : COBBLE_ID);
    }
  }

  private update(x: number, y: number, z: number): void {
    const host = this.host;
    if (!host.canEdit(x, y, z)) return;
    let id = host.getBlock(x, y, z);
    if (WATER_AMOUNT[id] === 0) return;
    if (IS_WATER_SOURCE[id] === 0) {
      const next = this.newLiquid(x, y, z);
      if (next !== id) {
        id = next;
        host.setBlock(x, y, z, next);
        if (next === AIR) return;
        this.schedule(x, y, z);
      }
    }
    this.spread(x, y, z, id);
  }

  /** The water a non-source cell should hold, judged from its neighbours (AIR = none). */
  private newLiquid(x: number, y: number, z: number): number {
    const host = this.host;
    let max = 0;
    let sources = 0;
    for (let d = 0; d < 4; d++) {
      const nid = host.getBlock(x + DX[d], y, z + DZ[d]);
      const amount = WATER_AMOUNT[nid];
      if (amount === 0) continue;
      if (IS_WATER_SOURCE[nid] === 1) sources++;
      if (amount > max) max = amount;
    }
    if (sources >= 2) {
      const below = host.getBlock(x, y - 1, z);
      if (IS_SOLID[below] === 1 || IS_WATER_SOURCE[below] === 1) return WATER_ID;
    }
    if (WATER_AMOUNT[host.getBlock(x, y + 1, z)] > 0) return FALLING_WATER_ID;
    return max > 1 ? FLOWING_WATER_IDS[max - 1] : AIR;
  }

  /** Can water occupy this block at all (air, plants, other water)? */
  private passable(x: number, y: number, z: number, id: number): boolean {
    return IS_SOLID[id] === 0 && id !== SUGAR_CANE_ID && this.host.canEdit(x, y, z);
  }

  /** Can water flow into the cell: open, and not already holding water. */
  private canSpreadTo(x: number, y: number, z: number): boolean {
    const id = this.host.getBlock(x, y, z);
    return WATER_AMOUNT[id] === 0 && this.passable(x, y, z, id);
  }

  /** Can a flow run through the cell on its way to a drop (anything but walls and sources)? */
  private canPassThrough(x: number, y: number, z: number): boolean {
    const id = this.host.getBlock(x, y, z);
    return IS_WATER_SOURCE[id] === 0 && this.passable(x, y, z, id);
  }

  /** Would water in this cell drop into the one below? */
  private isHole(x: number, y: number, z: number): boolean {
    const below = this.host.getBlock(x, y - 1, z);
    return WATER_AMOUNT[below] > 0 || this.passable(x, y - 1, z, below);
  }

  private spread(x: number, y: number, z: number, id: number): void {
    if (this.canSpreadTo(x, y - 1, z)) {
      this.host.setBlock(x, y - 1, z, this.newLiquid(x, y - 1, z));
      if (this.sourceNeighbours(x, y, z) >= 3) this.spreadToSides(x, y, z, id);
    } else if (IS_WATER_SOURCE[id] === 1 || !this.isHole(x, y, z)) {
      this.spreadToSides(x, y, z, id);
    }
  }

  private sourceNeighbours(x: number, y: number, z: number): number {
    let n = 0;
    for (let d = 0; d < 4; d++) n += IS_WATER_SOURCE[this.host.getBlock(x + DX[d], y, z + DZ[d])];
    return n;
  }

  private spreadToSides(x: number, y: number, z: number, id: number): void {
    const amount = id === FALLING_WATER_ID ? 7 : WATER_AMOUNT[id] - 1;
    if (amount <= 0) return;

    // sides on the shortest path to a drop; every open side when there is none
    const dirs = this.spreadDirs;
    const ids = this.spreadIds;
    dirs.length = 0;
    ids.length = 0;
    let min = NO_SLOPE;
    for (let d = 0; d < 4; d++) {
      const nx = x + DX[d];
      const nz = z + DZ[d];
      if (!this.canPassThrough(nx, y, nz)) continue;
      const dist = this.isHole(nx, y, nz) ? 0 : this.slopeDistance(nx, y, nz, 1, OPPOSITE[d]);
      if (dist < min) {
        dirs.length = 0;
        ids.length = 0;
      }
      if (dist <= min) {
        dirs.push(d);
        ids.push(this.newLiquid(nx, y, nz));
        min = dist;
      }
    }
    for (let i = 0; i < dirs.length; i++) {
      const nx = x + DX[dirs[i]];
      const nz = z + DZ[dirs[i]];
      if (ids[i] !== AIR && this.canSpreadTo(nx, y, nz)) this.host.setBlock(nx, y, nz, ids[i]);
    }
  }

  /** Steps from this cell to the nearest drop, searching up to SLOPE_FIND_DISTANCE away. */
  private slopeDistance(x: number, y: number, z: number, depth: number, from: number): number {
    let min = NO_SLOPE;
    for (let d = 0; d < 4; d++) {
      if (d === from) continue;
      const nx = x + DX[d];
      const nz = z + DZ[d];
      if (!this.canPassThrough(nx, y, nz)) continue;
      if (this.isHole(nx, y, nz)) return depth;
      if (depth < SLOPE_FIND_DISTANCE) {
        const dist = this.slopeDistance(nx, y, nz, depth + 1, OPPOSITE[d]);
        if (dist < min) min = dist;
      }
    }
    return min;
  }
}

/** Integer key of a block cell (|x|, |z| < 2^20, y < 256). */
function cellKey(x: number, y: number, z: number): number {
  return ((x + 0x100000) * 0x200000 + (z + 0x100000)) * 256 + y;
}
